import React, { FunctionComponent } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as TextSize } from '../../atoms/bodyText/BodyText'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { IS_WEB } from '../../constants'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const conversationStarterMessages = defineMessages({
  navigateStress: {
    defaultMessage: 'Navigate stress',
    description: 'Conversation starter prompt for navigating stress',
  },
  tackleChallenge: {
    defaultMessage: 'Tackle a challenge',
    description: 'Conversation starter prompt for tackling a challenge',
  },
  talkAboutFeelings: {
    defaultMessage: 'Talk about how I\u2019m feeling',
    description: 'Conversation starter prompt for talking about feelings',
  },
  reflectOnRelationship: {
    defaultMessage: 'Reflect on a relationship',
    description: 'Conversation starter prompt for reflecting on a relationship',
  },
})

const Container = styled(View)<{ theme: ThemeType }>(({ theme: { colors, spacing, breakpoints, isAuroraTheme } }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(
    isAuroraTheme,
    colors.backgroundSecondary,
    IS_WEB ? colors.backgroundSecondary : 'transparent',
  ),
  paddingHorizontal: breakpoints.isMobileSized ? spacing['24px'] : spacing['16px'],
  paddingTop: spacing['8px'],
  paddingBottom: spacing['24px'],
  gap: spacing['8px'],
  alignItems: 'flex-end',
}))

const PromptButton = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.cardBackgroundPrimary,
  borderColor: colors.cardOutlinePrimary,
  borderWidth: 1.5,
  borderRadius: 100,
  paddingHorizontal: spacing['16px'],
  minHeight: 40,
  justifyContent: 'center',
  alignItems: 'center',
}))

export interface ConversationStartersProps {
  onSelect: (text: string) => void
}

export const ConversationStarters: FunctionComponent<ConversationStartersProps> = ({ onSelect }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const prompts = [
    conversationStarterMessages.navigateStress,
    conversationStarterMessages.tackleChallenge,
    conversationStarterMessages.talkAboutFeelings,
    conversationStarterMessages.reflectOnRelationship,
  ]

  return (
    <Container testID={tID('ConversationStarters')}>
      {prompts.map((prompt, index) => {
        const text = formatMessage(prompt)
        return (
          <PromptButton
            key={index}
            onPress={() => onSelect(text)}
            testID={`ConversationStarters-prompt-${index}`}
            pressedOpacity={0.7}
          >
            <BodyText text={text} size={TextSize.DEFAULT} color={colors.textPrimary} />
          </PromptButton>
        )
      })}
    </Container>
  )
}
